import { useEffect, useMemo, useRef, useState } from 'react';
import '../../assets/styles/pedometer.scss';
import PedometerRewardRail from './PedometerRewardRail';
import PedometerSwipeHint from './PedometerSwipeHint';
import PedometerSceneFx from './PedometerSceneFx';
import { usePedometerBgm } from '../../hooks/usePedometerBgm';
import dayBg from '../../assets/images/pedometer/bg-day.png';
import nightBg from '../../assets/images/pedometer/bg-night.png';
import moonNight from '../../assets/images/pedometer/moon-night.png';
import catIdle from '../../assets/images/pedometer/cat-idle.gif';
import catIdleNight from '../../assets/images/pedometer/cat-idle-night.gif';
import catWalk from '../../assets/images/pedometer/cat-walk.gif';
import catWalkNight from '../../assets/images/pedometer/cat-walk-night.gif';
import catShadow from '../../assets/images/pedometer/shadow.png';
import soundOn from '../../assets/images/pedometer/sound_on.png';
import soundOff from '../../assets/images/pedometer/sound_off.png';
import clickChevron from '../../assets/images/pedometer/permission-click-chevron.svg';
import { isPedometerNativeSupported } from '../../services/pedometerNative';

const GOAL_STEPS = 10000;
/** 만보기 패널 활성 후 idle 유지 → walking 전환까지 */
const SCENE_WALK_DELAY_MS = 3000;
const WALK_MESSAGES = ['오늘도 천천히 걸어요', '꽃 구경하며 걸어요', '바람이 좋아요', '조금만 더 걸어볼까요'];

/** 18시부터 다음 날 6시까지는 밤 배경 */
export function isNightTime(date = new Date()) {
  const hour = date.getHours();
  return hour >= 18 || hour < 6;
}

function bubbleText({ steps, hasOpen, walking }) {
  if (steps >= GOAL_STEPS) {
    return '오늘 만보를 다 걸었어요!';
  }
  if (hasOpen) {
    return '어? 여기 뭐가 있어요!';
  }
  if (walking) {
    return WALK_MESSAGES[Math.floor(steps / 1000) % WALK_MESSAGES.length];
  }
  return '…음~ 냐옹';
}

function catSprite({ night, walking }) {
  if (walking) {
    return night ? catWalkNight : catWalk;
  }
  return night ? catIdleNight : catIdle;
}

function PlaygroundPedometerCard({
  today,
  steps = 0,
  permission = 'granted',
  needsHealthAppGuide = false,
  onRequestPermission,
  onOpenHealthApp,
  onSegmentTap,
  active = true,
  onSceneVisibleChange,
  appInfo,
}) {
  const [night, setNight] = useState(() => isNightTime());
  const [walking, setWalking] = useState(false);
  const walkTimerRef = useRef(null);

  useEffect(() => {
    const timer = window.setInterval(() => setNight(isNightTime()), 60000);
    return () => window.clearInterval(timer);
  }, []);

  /*
   * [보관] 센서/표기 걸음이 늘어나면 워킹 GIF → 12초 후 idle
   * HC/HK only 전환으로 센서 추정은 제거됨. 화면 노출 기반 walking 사용 중.
   *
   * const WALK_IDLE_MS = 12000;
   * const lastStepsRef = useRef(steps);
   * const readyRef = useRef(false);
   * useEffect(() => {
   *   if (!readyRef.current) {
   *     readyRef.current = true;
   *     lastStepsRef.current = steps;
   *     return;
   *   }
   *   if (steps > lastStepsRef.current) {
   *     setWalking(true);
   *     window.clearTimeout(walkTimerRef.current);
   *     walkTimerRef.current = window.setTimeout(() => setWalking(false), WALK_IDLE_MS);
   *   }
   *   lastStepsRef.current = steps;
   * }, [steps]);
   */

  const segments = useMemo(() => (Array.isArray(today?.segments) ? today.segments : []), [today]);
  const headIndex = useMemo(() => {
    const index = segments.findIndex((segment) => segment.status !== 'DONE');
    return index < 0 ? Math.max(0, segments.length - 1) : index;
  }, [segments]);
  const hasOpen = segments.some((segment) => segment.status === 'OPEN');

  const granted = permission === 'granted';
  const { sceneRef, sceneVisible, pageVisible, bgmOn, toggleBgm } = usePedometerBgm({ night });
  // BGM pause와 동일: 씬이 뷰포트에 보이고(페이지 포그라운드)일 때만 활성
  const sceneLive = sceneVisible && pageVisible;
  const fxActive = active && sceneLive;

  // 씬이 보일 때(BGM이 재생될 수 있는 시점) 3초 idle → 이후 walking / 숨기면 idle
  useEffect(() => {
    window.clearTimeout(walkTimerRef.current);
    if (!sceneLive) {
      setWalking(false);
      return undefined;
    }
    setWalking(false);
    walkTimerRef.current = window.setTimeout(() => setWalking(true), SCENE_WALK_DELAY_MS);
    return () => window.clearTimeout(walkTimerRef.current);
  }, [sceneLive]);

  useEffect(() => () => window.clearTimeout(walkTimerRef.current), []);

  const nativeSupported = isPedometerNativeSupported(appInfo);
  const message = nativeSupported ? bubbleText({ steps, hasOpen, walking }) : '곧 만보기가 오픈될 예정이에요!';
  const catSrc = catSprite({ night, walking });

  useEffect(() => {
    onSceneVisibleChange?.(sceneVisible);
  }, [sceneVisible, onSceneVisibleChange]);

  return (
    <div
      ref={sceneRef}
      className={`pdm-scene relative h-full w-full overflow-hidden${night ? ' pdm-scene--night' : ''}${walking ? ' pdm-scene--walking' : ''}`}
    >
      {/* 동일 배경 2장 가로 루프 — walking 때만 스크롤 (고양이 고정, 풍경이 지나감) */}
      <div className="pdm-scene__bg" aria-hidden="true">
        <div className="pdm-scene__bg-track">
          <img src={night ? nightBg : dayBg} alt="" className="pdm-scene__bg-img" />
          <img src={night ? nightBg : dayBg} alt="" className="pdm-scene__bg-img" />
        </div>
      </div>

      {/* 달은 분리 에셋 · 고정 (배경 패럴랙스와 분리) */}
      {night && (
        <img
          src={moonNight}
          alt=""
          className="pointer-events-none absolute right-[21%] top-[6%] z-[1] w-[40%]"
        />
      )}

      <PedometerSceneFx night={night} active={fxActive} />

      {/* 헤더(걸음수) 아래에 두어 Figma처럼 겹치지 않게 한다 */}
      <button
        type="button"
        className="pdm-bgm absolute right-3 top-[3rem] z-[5]"
        onClick={toggleBgm}
        aria-label={bgmOn ? '배경음악 끄기' : '배경음악 켜기'}
      >
        <img src={bgmOn ? soundOn : soundOff} alt="" className="pdm-bgm__icon" />
      </button>

      {!granted && (
        <button
          type="button"
          className="pdm-permission-nudge"
          onClick={() => onRequestPermission?.()}
          aria-label="건강 앱 걸음 권한 허용하기"
        >
          <div>
            <p className="pdm-permission-nudge__title">
              <span className="pdm-permission-nudge__em">건강 앱 권한</span>
              을 허용하고
              <br />
              <span className="pdm-permission-nudge__em">만보기</span>를 시작해보세요!
            </p>
            <p className="pdm-permission-nudge__desc">
              <span className="pdm-permission-nudge__em">걸음 보상</span>
              부터 만보기 <span className="pdm-permission-nudge__em">이벤트</span>
              까지
              <br />
              참여할 수 있어요.
            </p>
          </div>
          <span className="pdm-permission-nudge__cta">
            Click here
            <img src={clickChevron} alt="" className="pdm-permission-nudge__chevron" />
          </span>
        </button>
      )}

      {granted && needsHealthAppGuide && (
        <button
          type="button"
          className="pdm-permission-nudge"
          onClick={() => onOpenHealthApp?.()}
          aria-label="건강 앱에서 아이폰 걸음 켜기"
        >
          <div>
            <p className="pdm-permission-nudge__title">
              <span className="pdm-permission-nudge__em">건강 앱</span>을 켜고
              <br />
              <span className="pdm-permission-nudge__em">아이폰 걸음</span>을 공유해 주세요!
            </p>
            <p className="pdm-permission-nudge__desc">
              건강 앱에서 <span className="pdm-permission-nudge__em">아이폰 걸음</span>을
              <br />
              켜야 <span className="pdm-permission-nudge__em">만보기</span>에 참여할 수 있어요.
            </p>
          </div>
          <span className="pdm-permission-nudge__cta">
            Click here
            <img src={clickChevron} alt="" className="pdm-permission-nudge__chevron" />
          </span>
        </button>
      )}

      {granted && !needsHealthAppGuide && (
        <div className="pdm-bubble left-1/2 bottom-[47%] z-[3] -translate-x-1/2">
          <span>{message}</span>
        </div>
      )}

      <img
        src={catShadow}
        alt=""
        className={`pdm-cat-shadow absolute left-[46%] bottom-[34%] z-[2] w-[9%] opacity-50${walking ? ' pdm-cat-shadow--walk' : ''}`}
      />
      <img
        key={catSrc}
        src={catSrc}
        alt=""
        className={`pdm-cat left-[46.5%] bottom-[34%] z-[3] w-[10%]${walking ? ' pdm-cat--walk' : ''}`}
      />

      <PedometerSwipeHint active={active} />

      <PedometerRewardRail
        className="left-[4.6%] bottom-[7%] w-[90.8%] z-[4]"
        style={{ height: 'calc(var(--pdm-u) * 17)' }}
        segments={segments}
        headIndex={headIndex}
        steps={steps}
        onSegmentTap={onSegmentTap}
      />
    </div>
  );
}

export default PlaygroundPedometerCard;
